import { useEffect, ReactElement, Fragment, useState } from 'react';
import { Divider, List, Typography } from '@mui/material';
import { useFetchFromService, usePageTitle } from 'hooks';
import { useStore } from 'providers';
import { Transaction, TransactionDescription, TransactionType, Transactions } from 'models';
import Layout from 'layouts';
import Loader from 'components/Loader';
import Points from 'components/Points';
import Markdown from 'components/Markdown';
import ReferAndEarnIcon from 'assets/icons/ReferAndEarnIcon';
import ZizziCoinWithPlus from 'assets/icons/ZizziCoinWithPlus';
import {
  ShoppingBagOutlined,
  Add,
  ChangeCircleOutlined,
  LocalPizza,
  AlarmOnOutlined,
  ShoppingCartOutlined,
  PendingActionsOutlined,
} from '@mui/icons-material';
import {
  StyledContainer,
  StyledPaper,
  StyledStack,
  Label,
  StyledChip,
  StyledListItem,
  StyledListItemIcon,
  StyledListItemText,
  ExptyViewWrapper,
  EmptyViewTitle,
  StyledSingleAccordion,
} from './ZsActivity.styles';
import { zsActivityTerms } from './ZsActivity.copy';

type ActivityFilter = 'history' | 'expired';

interface FilterIconProps {
  type: ActivityFilter;
}

interface ActivityIconProps {
  type: TransactionDescription;
}

interface SymbolProps {
  type: TransactionType;
}

interface FilterProps {
  options: ActivityFilter[];
  currentFilter: ActivityFilter;
  onClick: (option: ActivityFilter) => void;
}

interface ActivityListProps {
  transactions: Transaction[];
}

const filterIcons: Record<string, ReactElement> = {
  history: <PendingActionsOutlined />,
  expired: <AlarmOnOutlined />,
};

const activityIcons: Record<TransactionDescription, ReactElement> = {
  default: <ChangeCircleOutlined />,
  'Bonus Zs': <ReferAndEarnIcon />,
  'Click & Collect': <ShoppingBagOutlined />,
  Delivery: <ShoppingCartOutlined />,
  'Referral Reward': <ReferAndEarnIcon />,
  'Zs Adjustment': <ChangeCircleOutlined />,
  'Added Zs': <Add />,
  'Perk Purchase': <ShoppingBagOutlined />,
  'Zizzi Dine-In': <LocalPizza />,
  'Welcome Zs': <LocalPizza />,
  '1st Visit Completion Bonus': <LocalPizza />,
  'Expired Zs': <AlarmOnOutlined />,
};

const initialTransactions: Transactions = {
  history: [],
  expired: [],
};

const FilterIcon = ({ type }: FilterIconProps) => filterIcons[type];
const ActivityIcon = ({ type }: ActivityIconProps) => activityIcons[type] || activityIcons.default;
const PlusSymbol = ({ type }: SymbolProps) => (type === 'Earning' ? <>+</> : null);

const Filter = ({ options, currentFilter, onClick }: FilterProps) => (
  <StyledStack direction="row" spacing={1}>
    <Label variant="h6">Filter:</Label>
    {options.map((option) => (
      <StyledChip
        key={option}
        label={option}
        icon={<FilterIcon type={option} />}
        onClick={() => onClick(option)}
        color={option === currentFilter ? 'primary' : undefined}
        aria-label="Filter tag"
      />
    ))}
  </StyledStack>
);

const ActivityList = ({ transactions }: ActivityListProps) => (
  <List>
    <Divider />
    {transactions.map(({ id, type, description, date, points }: Transaction) => (
      <Fragment key={id}>
        <StyledListItem>
          <StyledListItemIcon>
            <ActivityIcon type={description} />
          </StyledListItemIcon>
          <StyledListItemText primary={description} secondary={date} />
          <PlusSymbol type={type} />
          <Points points={points} />
        </StyledListItem>
        <Divider />
      </Fragment>
    ))}
  </List>
);

const EmptyView = () => (
  <ExptyViewWrapper>
    <ZizziCoinWithPlus />
    <EmptyViewTitle variant="h3">Your Z Balance is empty...</EmptyViewTitle>
    <Typography variant="body1">
      All Zillionaires have to start somewhere! Show our team your code to start earning Zs.
    </Typography>
  </ExptyViewWrapper>
);

const ZsActivity = () => {
  usePageTitle('Zs Activity');

  const { store } = useStore();
  const { transactions } = store;

  const fetchTransactions = useFetchFromService('transactions');

  useEffect(() => {
    if (!transactions) fetchTransactions();
  }, [transactions, fetchTransactions]);

  const [filter, setFilter] = useState<ActivityFilter>('history');

  const activityList = transactions || initialTransactions;
  const filterOptions = Object.keys(activityList) as ActivityFilter[];
  const filteredTransactions = activityList[filter];

  const handleOnFilter = (option: ActivityFilter) => setFilter(option);

  const terms = zsActivityTerms[filter];

  const shouldShowEmptyView = !activityList.history.length && filter === 'history';

  return (
    <Layout ariaLabel="Zs Activity page">
      {transactions ? (
        <StyledContainer>
          <StyledPaper>
            <Typography variant="h1">Zs Activity</Typography>
            <Filter options={filterOptions} currentFilter={filter} onClick={handleOnFilter} />
            <ActivityList transactions={filteredTransactions} />
            {shouldShowEmptyView && <EmptyView />}
            <StyledSingleAccordion title={terms.title}>
              <Markdown>{terms.text}</Markdown>
            </StyledSingleAccordion>
          </StyledPaper>
        </StyledContainer>
      ) : (
        <Loader />
      )}
    </Layout>
  );
};

export default ZsActivity;
